import {
  Component,
  useRef,
  useState,
  type CSSProperties,
  type ErrorInfo,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { swatchHex } from "./color";
import { graphLayout, type GraphEdgeDrawing, type GraphLayout, type GraphNodeDrawing } from "./geometry";
import { diskRun, GlyphView, isDiskGlyph, isPegStack, isSlot, jugGroup, JugsView, puzzleColor, ShapeView, TextFit, Unsupported } from "./glyphs";
import styles from "./PuzzleView.module.css";
import {
  describeLaunchBoard,
  HiveBoardView,
  isSentenceBoard,
  LaunchGridBoard,
  launchLayoutFor,
  SceneBoardView,
  SentenceBoardView,
  WheelBoardView,
  wantsLaunchGrid,
  type LaunchContext,
} from "./launch/boards";
import { LaunchAnswerView } from "./launch/inputs";
import { useLaunchPlay } from "./launch/play";
import { ScratchLayer } from "./launch/scratch";
import { LaunchWatch } from "./launch/watch";
import type {
  AnalogyBoard,
  Answer,
  Board,
  CardsBoard,
  CellRef,
  CellsGlyph,
  ChoiceAnswerSpec,
  Glyph,
  GraphBoard,
  GridBoard,
  GridCell,
  MatrixBoard,
  PairBoard,
  PhaseName,
  PuzzleSpec,
  ScatterBoard,
  ScatterItem,
  Selection,
  SequenceBoard,
  ShapeGlyph,
  SingleBoard,
  SpeakerCard,
  TextGlyph,
  WatchPhase,
} from "./types";
import { kindOf } from "./types";

export interface PuzzleViewProps {
  spec: PuzzleSpec;
  /** The correct answer; only shown when `reveal` is true. */
  answer?: Answer | null;
  /** Highlight the correct option / answer cells (and, in play mode, a wrong pick). */
  reveal?: boolean;
  /** Controlled phase for specs with a watch phase. Uncontrolled (starting at "watch") when omitted. */
  phase?: PhaseName;
  onPhaseChange?: (phase: PhaseName) => void;
  /** Hide the prompt line (e.g. when the host shows it elsewhere). */
  hidePrompt?: boolean;
  /** Smaller type and gaps for thumbnails. */
  compact?: boolean;
  className?: string;

  /**
   * Play mode: options and interactive grid cells become buttons (mouse, touch
   * and keyboard). The host owns the selection and decides when to submit.
   */
  interactive?: boolean;
  /** The player's current (or submitted) answer. */
  /** The player's current (or submitted) selection; launch kinds can be part-typed (see `Selection`). */
  selection?: Selection | null;
  /**
   * Every change of the selection. Launch kinds report part-typed selections too (a fill with empty
   * blanks), so submit only once `answerProgress(spec, selection).complete`.
   */
  onSelectionChange?: (selection: Selection | null) => void;
  /** Freeze input (e.g. while checking or after locking in). */
  disabled?: boolean;
  /**
   * Choice answers: options ruled out (a level hint). They're dimmed, `aria-disabled`, and can't be picked by
   * click or keyboard.
   */
  disabledOptions?: readonly string[];
  /**
   * Cell-set answers: cells placed for the player (a level hint). They show as selected, can't be removed, and
   * count towards the answer; the host keeps them in `selection`.
   */
  lockedCells?: readonly CellRef[];
  /** Show the Watch / Recall toggle. Defaults to on for previews, off in play mode. */
  showPhaseTabs?: boolean;
}

interface RenderContext {
  /** "r,c" keys of answer cells to highlight (cellSet reveal). */
  answerCells: Set<string> | null;
  /** Glyph to show inside the slot tile when the answer is revealed. */
  slotFill: Glyph | null;
  /** Play mode for grid boards (cellSet answers). */
  play: CellPlay | null;
  /** Rush Hour: the row whose right edge is the exit (drawn as an accent arrow). */
  exitRow?: number | null;
  /** Planning types drawn like board 19 rather than as plain tiles (see `planningDress`). */
  dress?: PlanningDress | null;
  /** Launch types (contracts/launch-types.md): fill / ordered-tap / Yes-No play and type layouts (launch/boards.tsx). */
  launch?: LaunchContext | null;
}

interface CellPlay {
  selected: Set<string>;
  /** "r,c" keys of placed cells: selected for good. */
  locked: Set<string>;
  count: number;
  disabled: boolean;
  revealed: boolean;
  toggle: (cell: CellRef) => void;
}

const cellKey = (r: number, c: number) => `${r},${c}`;

/**
 * Renders one PuzzleSpec v1 the way the app draws it: white-alpha tiles on black,
 * near-white ink, a dashed accent slot, lettered options, and a laser-yellow
 * stroke + glow on the correct answer when revealed.
 *
 * Pure and self-contained: depends only on React, this package, and the Aurora
 * CSS custom properties from tokens.css being present on the page. Safe to
 * render on the server (no effects, no browser APIs during render).
 */
export function PuzzleView({
  spec,
  answer,
  reveal = false,
  phase,
  onPhaseChange,
  hidePrompt = false,
  compact = false,
  className,
  interactive = false,
  selection = null,
  onSelectionChange,
  disabled = false,
  disabledOptions,
  lockedCells,
  showPhaseTabs,
}: PuzzleViewProps) {
  const [ownPhase, setOwnPhase] = useState<PhaseName>("watch");
  const phases = Array.isArray(spec?.phases) ? spec.phases : [];
  const watch = phases.find((p): p is WatchPhase => kindOf(p) === "watch");
  const unknownPhases = phases.filter((p) => !["watch", "recall"].includes(kindOf(p)));
  const activePhase: PhaseName = watch ? (phase ?? ownPhase) : "recall";
  const setPhase = (p: PhaseName) => {
    if (phase === undefined) setOwnPhase(p);
    onPhaseChange?.(p);
  };
  const tabs = showPhaseTabs ?? !interactive;
  // Launch answer kinds (number, digits, fill, sequence, rows); null for choice and cellSet.
  const launchPlay = useLaunchPlay({
    answerSpec: spec && typeof spec === "object" ? spec.answer : undefined,
    selection,
    shown: reveal ? answer : null,
    onSelectionChange,
    interactive,
    locked: disabled || reveal,
  });

  if (!spec || typeof spec !== "object") {
    return (
      <div className={[styles.root, className].filter(Boolean).join(" ")}>
        <Unsupported kind={kindOf(spec)} detail="not a puzzle spec" />
      </div>
    );
  }

  const answerSpec = spec.answer;
  const shown = reveal && answer ? answer : null;
  const inWatch = Boolean(watch) && activePhase === "watch";
  const board: Board | undefined = inWatch ? watch?.board : spec.board;
  const isCellSet = kindOf(answerSpec) === "cellSet";

  const selectedCells = new Set(
    selection?.kind === "cellSet" && Array.isArray(selection.value)
      ? selection.value.map((c) => cellKey(Number(c?.[0]), Number(c?.[1])))
      : [],
  );
  const count = isCellSet ? Math.max(1, Math.trunc(Number((answerSpec as { count?: unknown }).count) || 1)) : 0;
  const locked = disabled || reveal;
  const placed = new Set((lockedCells ?? []).map((c) => cellKey(Number(c?.[0]), Number(c?.[1]))));
  const ruledOut = new Set((disabledOptions ?? []).map(String));
  const toggleCell = (cell: CellRef) => {
    if (locked || !onSelectionChange) return;
    const key = cellKey(cell[0], cell[1]);
    if (placed.has(key)) return;
    const current =
      selection?.kind === "cellSet" && Array.isArray(selection.value) ? (selection.value as CellRef[]) : ([] as CellRef[]);
    let next: CellRef[];
    if (selectedCells.has(key)) next = current.filter((c) => cellKey(c[0], c[1]) !== key);
    else if (current.length < count) next = [...current, cell];
    else if (count === 1) next = [cell];
    else return;
    onSelectionChange(next.length ? { kind: "cellSet", value: next } : null);
  };

  const ctx: RenderContext = {
    answerCells:
      shown?.kind === "cellSet" && Array.isArray(shown.value)
        ? new Set(shown.value.map((c) => cellKey(Number(c?.[0]), Number(c?.[1]))))
        : null,
    slotFill:
      shown?.kind === "choice" && kindOf(answerSpec) === "choice"
        ? ((answerSpec as ChoiceAnswerSpec).options ?? []).find((o) => o?.id === shown.value)?.glyph ?? null
        : (launchPlay?.slotFill ?? null),
    play:
      interactive && isCellSet && !inWatch
        ? { selected: selectedCells, locked: placed, count, disabled: locked, revealed: reveal, toggle: toggleCell }
        : null,
    exitRow: rushHourExit(spec),
    dress: planningDress(spec),
    launch: { play: inWatch ? null : launchPlay, layout: launchLayoutFor(spec.type) },
  };

  return (
    <div
      className={[styles.root, compact ? styles.compact : "", className].filter(Boolean).join(" ")}
      data-palette={spec.palette}
      data-phase={watch ? activePhase : undefined}
      data-interactive={interactive || undefined}
    >
      {!hidePrompt && spec.prompt?.text ? (
        <div className={styles.prompt}>
          <span>{spec.prompt.text}</span>
          {/* The detail can be the recall question (Mental Juggle): never shown while watching. */}
          {spec.prompt.detail && !inWatch ? <span className={styles.promptDetail}>{spec.prompt.detail}</span> : null}
        </div>
      ) : null}

      {watch && tabs ? (
        <div className={styles.phases} role="tablist" aria-label="Phase">
          <button
            type="button"
            role="tab"
            aria-selected={activePhase === "watch"}
            className={styles.phase}
            onClick={() => setPhase("watch")}
          >
            <span className={styles.phaseIndex}>1</span>
            Watch
            <span className={styles.phaseMeta}>{formatSeconds(watch.durationMs)}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePhase === "recall"}
            className={styles.phase}
            onClick={() => setPhase("recall")}
          >
            <span className={styles.phaseIndex}>2</span>
            Recall
          </button>
        </div>
      ) : null}
      {unknownPhases.map((p, i) => (
        <Unsupported key={i} kind={`phase.${kindOf(p)}`} />
      ))}

      <PartBoundary label={`board.${kindOf(board)}`} resetKey={`${spec.id}|${activePhase}`}>
        <section
          className={styles.board}
          aria-label={spec.accessibility?.boardDescription ?? describeBoard(board, inWatch)}
          data-kind={kindOf(board)}
        >
          {inWatch && !watch?.board ? (
            <div className={styles.note}>Watch board omitted: competitive modes fetch it with a single-use token.</div>
          ) : (
            <ScratchLayer enabled={Boolean(spec.hints?.scratch) && interactive && !inWatch && !ctx.play} preview={Boolean(spec.hints?.scratch) && !interactive && !inWatch}>
              <LaunchWatch active={inWatch && interactive} board={board} durationMs={watch?.durationMs}>
                <BoardView board={board} ctx={inWatch ? { answerCells: null, slotFill: null, play: null, exitRow: ctx.exitRow, dress: ctx.dress, launch: { play: null, layout: ctx.launch!.layout } } : ctx} />
              </LaunchWatch>
            </ScratchLayer>
          )}
        </section>
      </PartBoundary>

      {!inWatch ? (
        <PartBoundary label={`answer.${kindOf(answerSpec)}`} resetKey={spec.id}>
          {launchPlay ? (
            <LaunchAnswerView play={launchPlay} board={board} />
          ) : (
          <AnswerView
            answerSpec={answerSpec}
            shown={shown}
            interactive={interactive}
            selection={selection}
            disabled={locked}
            onSelect={(id) => !locked && !ruledOut.has(id) && onSelectionChange?.({ kind: "choice", value: id })}
            selectedCount={selectedCells.size}
            ruledOut={ruledOut}
          />
          )}
        </PartBoundary>
      ) : null}
    </div>
  );
}

/**
 * Rush Hour (content/README "Notes for renderers"): the exit is on the right edge of
 * the row the ember X car is in (row index 2 on generated boards). Null for any
 * other type or a board that isn't a grid.
 */
function rushHourExit(spec: PuzzleSpec): number | null {
  if (spec.type !== "planning.rush_hour" || kindOf(spec.board) !== "grid") return null;
  const board = spec.board as GridBoard;
  const cols = clampInt(board.cols, 1, 12, 6);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  const x = cells.findIndex(
    (c) => c?.fill === "ember" && kindOf(c.glyph) === "text" && String((c.glyph as TextGlyph).value ?? "").trim().toUpperCase() === "X",
  );
  return x >= 0 ? Math.floor(x / cols) : 2;
}

/**
 * Board 19 dressings for Planning types whose encodings reuse the Key & Door grid (content/README
 * "Planning & Strategy, second wave"), keyed on the type as the Rush Hour exit is:
 *   - `ice` (Ice Slide, P6): an icy floor (sky tint) with walls as solid blocks, not hatching;
 *   - `sokoban` (P7): a lighter floor, solid walls in the planning wall colour, boxes as crates
 *     (the amber square is the whole tile), accent rings for targets, the player P on ember;
 *   - `lightbot` (P8): holes as gaps with no tile, blue target tiles and the ember robot drawn solid
 *     (its arrow dark, facing where it points), and the legend sequence as small command chips.
 */
export type PlanningDress = "ice" | "sokoban" | "lightbot";

function planningDress(spec: PuzzleSpec): PlanningDress | null {
  switch (spec.type) {
    case "planning.ice_slide":
      return "ice";
    case "planning.sokoban":
      return "sokoban";
    case "planning.lightbot":
      return "lightbot";
    default:
      return null;
  }
}

/** Lightbot's robot faces where its arrow points: 0 up, 90 right, 180 down, 270 left. */
const FACING = ["up", "right", "down", "left"];
const facing = (g: Glyph | undefined) => FACING[((Math.round(Number((g as ShapeGlyph).rotation ?? 0) / 90) % 4) + 4) % 4]!;

function formatSeconds(ms: unknown): string {
  const n = typeof ms === "number" && Number.isFinite(ms) ? ms : 0;
  return `${(n / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------- boards

type TileState = "idle" | "slot" | "filled" | "correct" | "wrong" | "selected" | "dim";

function Tile({
  glyph,
  state = "idle",
  shape = "square",
  label,
  children,
}: {
  glyph?: Glyph | null;
  state?: TileState;
  shape?: "square" | "wide";
  label?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.tile} data-state={state} data-shape={shape} aria-label={label}>
      <div className={styles.tileInner}>{children ?? <GlyphView glyph={glyph} />}</div>
    </div>
  );
}

function BoardTile({ glyph, ctx, shape = "square" }: { glyph: Glyph | undefined; ctx: RenderContext; shape?: "square" | "wide" }) {
  if (isSlot(glyph)) {
    return ctx.slotFill ? (
      <Tile glyph={ctx.slotFill} state="filled" shape={shape} label="missing tile (answer revealed)" />
    ) : (
      <Tile glyph={glyph} state="slot" shape={shape} label="missing tile" />
    );
  }
  return <Tile glyph={glyph} shape={shape} />;
}

function BoardView({ board, ctx }: { board: Board | undefined; ctx: RenderContext }) {
  if (!board || typeof board !== "object") return <Unsupported kind={`board.${kindOf(board)}`} />;
  switch (board.kind) {
    case "none":
      return null;
    case "single":
      // Hidden Word (board 20 W5): the sentence set large, without a tile (launch/boards.tsx).
      if (ctx.launch?.layout === "sentence" && isSentenceBoard(board)) return <SentenceBoardView board={board} />;
      return <SingleBoardView board={board} ctx={ctx} />;
    case "sequence":
      return <SequenceBoardView board={board} ctx={ctx} />;
    case "matrix":
      return <MatrixBoardView board={board} ctx={ctx} />;
    case "analogy":
      return <AnalogyBoardView board={board} ctx={ctx} />;
    case "grid":
      return <GridBoardView board={board} ctx={ctx} />;
    case "pair":
      return <PairBoardView board={board} ctx={ctx} />;
    case "scatter":
      return <ScatterBoardView board={board} />;
    case "cards":
      return <CardsBoardView board={board} />;
    case "graph":
      return <GraphBoardView board={board} />;
    case "scene":
      return <SceneBoardView board={board} />;
    case "hive":
      return <HiveBoardView board={board} />;
    case "wheel":
      return <WheelBoardView board={board} />;
    default:
      return <Unsupported kind={`board.${kindOf(board)}`} />;
  }
}

/**
 * The board's accessible name when the spec has no `accessibility.boardDescription`.
 * Phase 5 boards describe themselves (never the answer); older boards keep the
 * generic name.
 */
export function describeBoard(board: Board | undefined, watching = false): string {
  switch (kindOf(board)) {
    case "scatter":
      return countLabel(Array.isArray((board as ScatterBoard).items) ? (board as ScatterBoard).items.length : 0, "item");
    case "cards":
      return describeCards(board as CardsBoard);
    case "pair": {
      const labels = pairLabels(board as PairBoard);
      return `Two boards: ${labels[0]} and ${labels[1]}`;
    }
    case "graph":
      return graphSummary(graphLayout(board as GraphBoard));
    default:
      // Launch boards (scene, hive, wheel, grid@2 regions, Kakuro clue grids) describe themselves.
      return describeLaunchBoard(board) ?? (watching ? "Watch board" : "Puzzle board");
  }
}

const countLabel = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Text in word / equation / label style reads better on a wide tile. */
function isWide(g: Glyph | undefined): boolean {
  if (kindOf(g) === "text") return ["equation", "word", "label"].includes((g as { style?: string }).style ?? "");
  if (kindOf(g) === "stack") return (g as { layout?: string }).layout !== "overlay";
  return false;
}

function SingleBoardView({ board, ctx }: { board: SingleBoard; ctx: RenderContext }) {
  const g = board.glyph;
  const jugs = jugGroup(g);
  if (jugs) {
    // Water Jug: the jugs stand on the board itself, drawn to one scale (no tile).
    return (
      <div className={styles.single}>
        <div className={styles.jugBoard}>
          <JugsView jugs={jugs} board />
        </div>
        {board.caption ? <div className={styles.caption}>{board.caption}</div> : null}
      </div>
    );
  }
  const lines = textLines(kindOf(g) === "stack" && (g as { layout?: string }).layout === "column" ? (g as { items?: unknown }).items : null);
  if (lines) {
    return (
      <div className={styles.single}>
        <TextLines lines={lines} />
        {board.caption ? <div className={styles.caption}>{board.caption}</div> : null}
      </div>
    );
  }
  const wide = isWide(g);
  return (
    <div className={styles.single} data-wide={wide || undefined}>
      {isSlot(g) ? <BoardTile glyph={g} ctx={ctx} /> : <Tile glyph={g} shape={wide ? "wide" : "square"} />}
      {board.caption ? <div className={styles.caption}>{board.caption}</div> : null}
    </div>
  );
}

function SequenceBoardView({ board, ctx }: { board: SequenceBoard; ctx: RenderContext }) {
  const items = Array.isArray(board.items) ? board.items : [];
  const chips = ctx.dress === "lightbot" ? commandChips(items) : null;
  if (chips) {
    // Lightbot (board 19 P8): the command chips double as the legend.
    return (
      <ul className={styles.chips} aria-label="Commands">
        {chips.map((c, i) => (
          <li key={i} className={styles.chip} aria-label={c.label}>
            <span aria-hidden>{c.text}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    // Seven or more items wrap into two rows (board 14 N1: eight as two rows of four).
    <div className={styles.sequence} style={{ "--n": items.length >= 7 ? Math.ceil(items.length / 2) : Math.max(items.length, 1) } as CSSProperties}>
      {items.map((g, i) => (
        <BoardTile key={i} glyph={g} ctx={ctx} />
      ))}
    </div>
  );
}

const COMMANDS: Record<string, string> = { F: "F, forward", L: "L, turn left", R: "R, turn right", "★": "Star, light the tile" };

/** A Lightbot legend: 1–8 short texts (F, L, R, ★), each with what it does when it is one of those. */
function commandChips(items: readonly Glyph[]): { text: string; label: string }[] | null {
  if (items.length < 1 || items.length > 8 || !items.every((g) => kindOf(g) === "text")) return null;
  const texts = items.map((g) => String((g as TextGlyph).value ?? "").trim());
  if (texts.some((t) => !t || [...t].length > 2)) return null;
  return texts.map((t) => ({ text: t, label: COMMANDS[t] ?? `Command ${t}` }));
}

function MatrixBoardView({ board, ctx }: { board: MatrixBoard; ctx: RenderContext }) {
  const rows = clampInt(board.rows, 1, 6, 3);
  const cols = clampInt(board.cols, 1, 6, 3);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  return (
    <div className={styles.matrix} style={{ "--cols": cols } as CSSProperties}>
      {Array.from({ length: rows * cols }, (_, i) => (
        <BoardTile key={i} glyph={cells[i] ?? { kind: "empty" }} ctx={ctx} />
      ))}
    </div>
  );
}

/**
 * board.analogy@1, "A : B :: C : ?". As on the boards (13 P1 Visual Analogy,
 * 20 W7 Word Analogy): the two pairs stack in two rows joined by "as"; word
 * pairs use wide tiles.
 */
function AnalogyBoardView({ board, ctx }: { board: AnalogyBoard; ctx: RenderContext }) {
  const left = Array.isArray(board.left) ? board.left : [];
  const right = Array.isArray(board.right) ? board.right : [];
  const wide = [...left, ...right].some((g) => isWide(g));
  const shape = wide ? "wide" : "square";
  const pair = (items: Glyph[], label: string) => (
    <div className={styles.analogyPair} role="group" aria-label={label}>
      <BoardTile glyph={items[0]} ctx={ctx} shape={shape} />
      <span className={styles.analogySep} aria-hidden>
        :
      </span>
      <BoardTile glyph={items[1]} ctx={ctx} shape={shape} />
    </div>
  );
  return (
    <div className={styles.analogy} data-wide={wide || undefined}>
      {pair(left, "First pair")}
      <span className={styles.analogyAs}>as</span>
      {pair(right, "Second pair")}
    </div>
  );
}

const COLUMN_LABELS = "ABCDEFGHIJKL";

const isPlainCell = (cell: GridCell) => !cell.fill && !cell.lit && !cell.interactive && typeof cell.region !== "number";
const isEmptyCell = (cell: GridCell) => isPlainCell(cell) && (!cell.glyph || kindOf(cell.glyph) === "empty");

/**
 * A clue ring (the Skyscrapers encoding): a grid of at least 4×4 whose four
 * corners are empty and whose other outer cells are empty or plain text, with
 * at least one clue. The ring is drawn as bare clues around the puzzle, and
 * labels and cell names count only the inner squares.
 */
export function hasClueRing(rows: number, cols: number, cells: readonly (GridCell | undefined)[]): boolean {
  if (rows < 4 || cols < 4) return false;
  const at = (r: number, c: number) => cells[r * cols + c] ?? {};
  const corners: CellRef[] = [
    [0, 0],
    [0, cols - 1],
    [rows - 1, 0],
    [rows - 1, cols - 1],
  ];
  if (!corners.every(([r, c]) => isEmptyCell(at(r, c)))) return false;
  let clues = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const outer = r === 0 || c === 0 || r === rows - 1 || c === cols - 1;
      const corner = (r === 0 || r === rows - 1) && (c === 0 || c === cols - 1);
      if (!outer || corner) continue;
      const cell = at(r, c);
      if (isEmptyCell(cell)) continue;
      if (!isPlainCell(cell) || kindOf(cell.glyph) !== "text") return false;
      clues++;
    }
  }
  return clues > 0;
}

const SIGNS: Record<string, "lt" | "gt"> = { "<": "lt", ">": "gt", "∧": "lt", "^": "lt", "˄": "lt", "∨": "gt", "˅": "gt", v: "gt" };
const signOf = (cell: GridCell | undefined) =>
  kindOf(cell?.glyph) === "text" ? SIGNS[String((cell!.glyph as TextGlyph).value ?? "").trim()] : undefined;

/**
 * Sign gutters (the Inequality Grid encoding): a (2n − 1) × (2n − 1) grid whose
 * odd rows and columns hold only plain sign texts (< > ∧ ∨) or nothing, with at
 * least one sign. Squares are the even-even cells; the gutters are drawn thin.
 */
export function hasSignGutters(rows: number, cols: number, cells: readonly (GridCell | undefined)[]): boolean {
  if (rows < 3 || cols < 3 || rows % 2 === 0 || cols % 2 === 0) return false;
  let signs = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (r % 2 === 0 && c % 2 === 0) continue;
      const cell = cells[r * cols + c] ?? {};
      if (isEmptyCell(cell)) continue;
      if (r % 2 === 1 && c % 2 === 1) return false;
      if (!isPlainCell(cell) || !signOf(cell)) return false;
      signs++;
    }
  }
  return signs > 0;
}

/**
 * Towers (Tower of Hanoi): every cell above the last row is empty or a disk (a
 * centred one-row bar, optionally numbered), the last row holds the peg labels,
 * and there is at least one disk. Drawn as pegs on a base, without tiles.
 */
export function hasTowers(rows: number, cols: number, cells: readonly (GridCell | undefined)[]): boolean {
  if (rows < 2 || cols < 2) return false;
  let disks = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = cells[r * cols + c] ?? {};
      if (!isPlainCell(cell)) return false;
      if (r === rows - 1) {
        if (kindOf(cell.glyph) !== "text" && !isEmptyCell(cell)) return false;
      } else if (!isEmptyCell(cell)) {
        if (!isDiskGlyph(cell.glyph)) return false;
        disks++;
      }
    }
  }
  return disks > 0;
}

/**
 * Lines of text meant to be read, not tiled: a column of 2+ plain texts where at
 * least one is longer than a short word (Logic Grid clues). Null otherwise.
 */
function textLines(items: unknown): string[] | null {
  if (!Array.isArray(items) || items.length < 2) return null;
  if (!items.every((g) => kindOf(g) === "text")) return null;
  const lines = items.map((g) => String((g as TextGlyph).value ?? ""));
  return lines.some((l) => [...l].length > 12) ? lines : null;
}

/**
 * board.grid@1. Two encodings are drawn as something other than tiles (and get
 * their own components, so each keeps a stable set of hooks): clue lines
 * (Logic Grid) and towers (Tower of Hanoi). Everything else is a tile grid.
 */
function GridBoardView({ board, ctx }: { board: GridBoard; ctx: RenderContext }) {
  // Launch grids: grid@2 regions, Kakuro clues, fill / ordered-tap / Yes-No play, type layouts (launch/boards.tsx).
  if (wantsLaunchGrid(board, ctx.launch)) return <LaunchGridBoard board={board} ctx={ctx} />;
  const rows = clampInt(board.rows, 1, 12, 4);
  const cols = clampInt(board.cols, 1, 12, 4);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  const lines =
    cols === 1 && cells.length >= rows && cells.slice(0, rows).every((c) => c && isPlainCell(c))
      ? textLines(cells.slice(0, rows).map((c) => c?.glyph))
      : null;
  if (lines) return <TextLines lines={lines} />;
  const hasRegions = cells.some((c) => typeof c?.region === "number");
  if (!hasRegions && !hasSignGutters(rows, cols, cells) && hasTowers(rows, cols, cells)) {
    return <TowersView rows={rows} cols={cols} cells={cells} />;
  }
  return <TileGridView board={board} ctx={ctx} />;
}

/**
 * Tower of Hanoi: one peg per column, disks stacked from the base up with no
 * gaps (a real tower has none), widths proportional to disk size with a floor
 * that still fits the number, pegs tall enough for every disk plus headroom,
 * the peg letters under the base. Each peg reads "Peg P: disks 2, 1, bottom to top".
 */
function TowersView({ rows, cols, cells }: { rows: number; cols: number; cells: readonly (GridCell | undefined)[] }) {
  const pegs = Array.from({ length: cols }, (_, c) => {
    const disks: { size: number; of: number; color: string; label: string }[] = [];
    for (let r = rows - 2; r >= 0; r--) {
      const g = cells[r * cols + c]?.glyph;
      const d = g ? diskInfo(g) : null;
      if (d) disks.push(d);
    }
    const name = kindOf(cells[(rows - 1) * cols + c]?.glyph) === "text" ? String((cells[(rows - 1) * cols + c]!.glyph as TextGlyph).value ?? "").trim() : "";
    return { name, disks };
  });
  const total = pegs.reduce((n, p) => n + p.disks.length, 0);
  const largest = Math.max(1, ...pegs.flatMap((p) => p.disks.map((d) => Math.max(d.size, d.of))));
  const levels = Math.max(rows - 1, total);
  return (
    <div className={styles.towers} style={{ "--levels": levels, "--pegs": cols } as CSSProperties} data-towers>
      {pegs.map((p, c) => {
        const label = `Peg ${p.name || c + 1}: ${
          p.disks.length === 0 ? "empty" : `${p.disks.length === 1 ? "disk" : "disks"} ${p.disks.map((d) => d.label).join(", ")}${p.disks.length > 1 ? ", bottom to top" : ""}`
        }`;
        return (
          <div key={c} className={styles.tower} role="img" aria-label={label} data-peg={p.name || c + 1}>
            <div className={styles.towerStack}>
              <span className={styles.towerPeg} aria-hidden />
              {p.disks.map((d, i) => (
                <span
                  key={i}
                  className={styles.towerDisk}
                  data-disk={d.size}
                  style={
                    {
                      "--level": i,
                      "--share": d.size / largest,
                      background: d.color,
                    } as CSSProperties
                  }
                >
                  {d.label}
                </span>
              ))}
            </div>
            <span className={styles.towerName}>{p.name}</span>
          </div>
        );
      })}
    </div>
  );
}

/** A tower disk's size (its bar is 2k − 1 of 2n − 1 cells wide: size k of n), colour and number. */
function diskInfo(g: Glyph): { size: number; of: number; color: string; label: string } | null {
  const bar = kindOf(g) === "cells" ? g : kindOf(g) === "stack" ? ((g as { items?: Glyph[] }).items ?? [])[0] : undefined;
  if (!bar || kindOf(bar) !== "cells") return null;
  const run = diskRun(bar as CellsGlyph);
  if (!run) return null;
  const size = (run.to - run.from + 2) / 2;
  const of = (clampInt((bar as CellsGlyph).cols, 1, 99, 1) + 1) / 2;
  const text = kindOf(g) === "stack" ? ((g as { items?: Glyph[] }).items ?? []).find((i) => kindOf(i) === "text") : undefined;
  const label = text ? String((text as TextGlyph).value ?? "").trim() : String(size);
  return { size, of, color: puzzleColor(run.color), label };
}

function TileGridView({ board, ctx }: { board: GridBoard; ctx: RenderContext }) {
  const rows = clampInt(board.rows, 1, 12, 4);
  const cols = clampInt(board.cols, 1, 12, 4);
  const cells = Array.isArray(board.cells) ? board.cells : [];
  const regionOf = (r: number, c: number) => cells[r * cols + c]?.region;
  const hasRegions = cells.some((c) => typeof c?.region === "number");
  const gutters = !hasRegions && hasSignGutters(rows, cols, cells);
  const ring = !hasRegions && !gutters && hasClueRing(rows, cols, cells);
  const onRing = (r: number, c: number) => ring && (r === 0 || c === 0 || r === rows - 1 || c === cols - 1);
  const inGutter = (r: number, c: number) => gutters && (r % 2 === 1 || c % 2 === 1);
  const play = ctx.play;
  // Cells flagged `interactive` take taps; if none are flagged, every cell does (except clue-ring cells).
  const anyFlagged = cells.some((c) => c?.interactive);
  // Mixed grids (Mine Deduction): given cells carry glyphs next to tappable hidden ones, so givens sit recessed.
  const mixed = anyFlagged && cells.some((c) => c && !c.interactive && c.glyph && kindOf(c.glyph) !== "empty");
  const tappableAt = (cell: GridCell, r: number, c: number) =>
    Boolean(play) && (anyFlagged ? Boolean(cell.interactive) : !onRing(r, c) && !inGutter(r, c));
  // Cell names ("B3") count the inner squares when the grid has a clue ring, and only the squares when it has gutters.
  const colName = (c: number) => COLUMN_LABELS[ring ? c - 1 : gutters ? c / 2 : c] ?? String(c + 1);
  const rowName = (r: number) => String(ring ? r : gutters ? r / 2 + 1 : r + 1);
  const gridRef = useRef<HTMLDivElement>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);

  const tappableKeys: string[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) if (tappableAt(cells[r * cols + c] ?? {}, r, c)) tappableKeys.push(cellKey(r, c));
  const rovingKey = focusKey && tappableKeys.includes(focusKey) ? focusKey : (tappableKeys[0] ?? null);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!play) return;
    const delta: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const d = delta[e.key];
    if (!d || !rovingKey) return;
    e.preventDefault();
    let [r, c] = rovingKey.split(",").map(Number) as [number, number];
    for (let step = 0; step < Math.max(rows, cols); step++) {
      r += d[0];
      c += d[1];
      if (r < 0 || c < 0 || r >= rows || c >= cols) return;
      const key = cellKey(r, c);
      if (tappableKeys.includes(key)) {
        setFocusKey(key);
        gridRef.current?.querySelector<HTMLElement>(`[data-cell="${key}"]`)?.focus();
        return;
      }
    }
  };

  const tiles: ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = cells[r * cols + c] ?? {};
      const key = cellKey(r, c);
      if (inGutter(r, c)) {
        tiles.push(<GutterCell key={key} cell={cell} r={r} c={c} name={(rr, cc) => `${colName(cc)}${rowName(rr)}`} />);
        continue;
      }
      const clue = onRing(r, c);
      const slot = isSlot(cell.glyph);
      // A slot cell (Skyscrapers' asked square) shows the revealed choice inside it.
      const slotFilled = slot && Boolean(ctx.slotFill);
      const glyph = slotFilled ? ctx.slotFill : cell.glyph;
      const isAnswer = (ctx.answerCells?.has(key) ?? false) || slotFilled;
      const isSelected = play?.selected.has(key) ?? false;
      const isPlaced = play?.locked.has(key) ?? false;
      const isWrong = Boolean(play?.revealed && ctx.answerCells && isSelected && !isAnswer);
      const swatch = kindOf(cell.glyph) === "swatch" ? (cell.glyph as { color?: string; tone?: unknown }) : null;
      const style: CSSProperties & Record<`--${string}`, string> = {};
      // Fills go through a custom property so selection and reveal states can still restyle the cell.
      if (cell.fill) style["--cell-fill"] = puzzleColor(cell.fill);
      if (swatch) style.background = swatchHex(swatch.color, swatch.tone);
      if (hasRegions) {
        const here = regionOf(r, c);
        style.borderRightWidth = c === cols - 1 ? 0 : regionOf(r, c + 1) !== here ? 3 : 1;
        style.borderBottomWidth = r === rows - 1 ? 0 : regionOf(r + 1, c) !== here ? 3 : 1;
        if (c < cols - 1 && regionOf(r, c + 1) !== here) style.borderRightColor = "var(--color-puzzle-ink)";
        if (r < rows - 1 && regionOf(r + 1, c) !== here) style.borderBottomColor = "var(--color-puzzle-ink)";
      }
      const hasGlyph = Boolean(cell.glyph) && kindOf(cell.glyph) !== "empty";
      // A letter or number on a solid colour (Rush Hour cars, Key & Door S/K/G) is a labelled piece: solid fill, dark ink.
      // A "?" (a hidden square) or any other glyph on a fill is drawn on a tint instead, so it stays legible.
      const labelOnFill =
        Boolean(cell.fill) &&
        cell.fill !== "muted" &&
        kindOf(cell.glyph) === "text" &&
        String((cell.glyph as TextGlyph).value ?? "").trim() !== "?" &&
        [undefined, "ink"].includes((cell.glyph as TextGlyph).color);
      if (labelOnFill) style["--glyph-ink"] = "var(--color-accent-ink)";
      const bare = isPegStack(cell.glyph) && isPlainCell(cell);
      // A muted fill with nothing on it that can't be tapped is a wall (mazes) or a covered square: hatched, as on board 19.
      const wall = cell.fill === "muted" && !cell.interactive && !cell.lit && !hasGlyph;
      // Board 19 dressings (planningDress): Ice Slide and Sokoban walls are solid blocks, Lightbot's are holes;
      // Lightbot's blue tiles and robot are solid pieces; Sokoban's amber squares are crates, its rings targets.
      const dress = ctx.dress ?? null;
      const shape = kindOf(cell.glyph) === "shape" ? (cell.glyph as ShapeGlyph).shape : null;
      const hole = dress === "lightbot" && wall;
      const block = (dress === "ice" || dress === "sokoban") && wall ? dress : null;
      const piece = dress === "lightbot" && Boolean(cell.fill) && cell.fill !== "muted" && shape !== null;
      const box = dress === "sokoban" && !cell.fill && shape === "square";
      const target = dress === "sokoban" && !cell.fill && shape === "ring";
      // The robot's arrow is dark on its ember tile (board 19 P8); the blue tile keeps its light diamond.
      if (piece && cell.fill !== "blue") style["--glyph-ink"] = "var(--color-accent-ink)";
      const dressed = hole
        ? " hole"
        : block
          ? " wall"
          : box
            ? " box"
            : target
              ? " target ring"
              : piece
                ? shape === "arrow"
                  ? ` robot facing ${facing(cell.glyph)}`
                  : ` ${cell.fill} tile`
                : "";
      const label = clue
        ? clueLabel(cell, r, c, rows, cols, colName, rowName)
        : `${colName(c)}${rowName(r)}${dressed}${cell.lit ? " lit" : ""}${slot ? (slotFilled ? " missing (answer revealed)" : " missing") : ""}${isPlaced ? " placed" : ""}${isAnswer ? " answer" : ""}${isWrong ? " wrong" : ""}`;
      const data = {
        "data-cell": key,
        "data-lit": cell.lit || undefined,
        "data-filled": cell.fill || swatch ? true : undefined,
        "data-fill": cell.fill ? true : undefined,
        // A filled cell that carries a glyph (a hidden "?" square) is tinted so the glyph stays legible.
        "data-tint": cell.fill && hasGlyph && !swatch && !labelOnFill && !piece ? true : undefined,
        "data-bare": bare || undefined,
        "data-wall": (wall && !hole && !block) || undefined,
        "data-hole": hole || undefined,
        "data-block": block ?? undefined,
        "data-piece": piece || undefined,
        "data-box": box || undefined,
        "data-target": target || undefined,
        "data-swatch": swatch ? true : undefined,
        "data-interactive": cell.interactive || undefined,
        "data-clue": clue || undefined,
        "data-slot": (slot && !slotFilled) || undefined,
        "data-answer": isAnswer || undefined,
        "data-selected": isSelected || isPlaced || undefined,
        "data-placed": isPlaced || undefined,
        "data-wrong": isWrong || undefined,
      };
      // A crate is the whole tile (board 19 P7), so its square isn't drawn again on top.
      const inner =
        glyph && !swatch && !box ? (
          <div className={styles.tileInner}>
            <GlyphView glyph={glyph} />
          </div>
        ) : null;
      if (play && tappableAt(cell, r, c)) {
        tiles.push(
          <button
            key={key}
            type="button"
            className={styles.gridCell}
            {...data}
            aria-label={label}
            aria-pressed={isSelected || isPlaced}
            aria-disabled={play.disabled || isPlaced || undefined}
            tabIndex={rovingKey === key ? 0 : -1}
            onFocus={() => setFocusKey(key)}
            onClick={() => play.toggle([r, c])}
            style={style}
          >
            {inner}
          </button>,
        );
      } else {
        tiles.push(
          <div key={key} className={styles.gridCell} {...data} aria-label={label} style={style}>
            {inner}
          </div>,
        );
      }
    }
  }

  const innerRows = ring ? rows - 2 : gutters ? (rows + 1) / 2 : rows;
  const innerCols = ring ? cols - 2 : gutters ? (cols + 1) / 2 : cols;
  const gridStyle: CSSProperties & Record<`--${string}`, string | number> = { "--cols": cols, "--rows": rows };
  if (gutters) {
    const track = (n: number) => Array.from({ length: n }, (_, i) => (i % 2 === 0 ? "minmax(0, 1fr)" : "var(--gutter)")).join(" ");
    gridStyle.gridTemplateColumns = track(cols);
    gridStyle["--squares"] = innerCols;
  }
  const grid = (
    <div
      ref={gridRef}
      className={styles.grid}
      data-regions={hasRegions || undefined}
      data-ring={ring || undefined}
      data-gutters={gutters || undefined}
      data-mixed={mixed || undefined}
      data-dress={ctx.dress ?? undefined}
      data-play={play ? true : undefined}
      role={play ? "group" : undefined}
      aria-label={play ? `Grid, ${innerRows} by ${innerCols}. Arrow keys move, Space or Enter toggles a square.` : undefined}
      onKeyDown={play ? onKeyDown : undefined}
      style={gridStyle}
    >
      {tiles}
    </div>
  );

  if (typeof ctx.exitRow === "number" && ctx.exitRow >= 0 && ctx.exitRow < rows) {
    // Rush Hour: an accent arrow off the right edge of the exit row.
    return (
      <div className={styles.exitWrap} style={{ "--cols": cols, "--rows": rows } as CSSProperties}>
        {grid}
        <div className={styles.exitRail}>
          {Array.from({ length: rows }, (_, r) =>
            r === ctx.exitRow ? (
              <span key={r} className={styles.exitMark} role="img" aria-label={`Exit, right of row ${r + 1}`} data-exit={r}>
                <svg viewBox="0 0 24 24" className={styles.svg}>
                  <path d="M4 12h15M13 6l6 6-6 6" fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            ) : (
              <span key={r} />
            ),
          )}
        </div>
      </div>
    );
  }

  if (!board.labels || gutters) return grid;
  return (
    <div className={styles.gridLabelled} style={{ "--cols": cols, "--rows": rows } as CSSProperties}>
      <span />
      <div className={styles.gridColLabels} aria-hidden>
        {Array.from({ length: cols }, (_, c) => (
          <span key={c}>{onRing(1, c) ? "" : colName(c)}</span>
        ))}
      </div>
      <div className={styles.gridRowLabels} aria-hidden>
        {Array.from({ length: rows }, (_, r) => (
          <span key={r}>{onRing(r, 1) ? "" : rowName(r)}</span>
        ))}
      </div>
      {grid}
    </div>
  );
}

/** "Clue 3, top of column B" for a clue-ring cell (or "No clue" when the side has none). */
function clueLabel(
  cell: GridCell,
  r: number,
  c: number,
  rows: number,
  cols: number,
  colName: (c: number) => string,
  rowName: (r: number) => string,
): string {
  const value = kindOf(cell.glyph) === "text" ? String((cell.glyph as { value?: unknown }).value ?? "") : "";
  const side =
    r === 0
      ? `top of column ${colName(c)}`
      : r === rows - 1
        ? `bottom of column ${colName(c)}`
        : c === 0
          ? `left of row ${rowName(r)}`
          : c === cols - 1
            ? `right of row ${rowName(r)}`
            : "";
  if ((r === 0 || r === rows - 1) && (c === 0 || c === cols - 1)) return "corner";
  return value ? `Clue ${value}, ${side}` : `No clue, ${side}`;
}

/** One gutter cell of a sign-gutter grid: a sign between two squares (read as "B1 is less than C1"), or nothing. */
function GutterCell({ cell, r, c, name }: { cell: GridCell; r: number; c: number; name: (r: number, c: number) => string }) {
  const sign = signOf(cell);
  const vertical = r % 2 === 1;
  const axis = r % 2 === 1 && c % 2 === 1 ? "cross" : vertical ? "row" : "col";
  if (!sign) return <div className={styles.gutterCell} data-gutter={axis} aria-hidden />;
  const [a, b] = vertical ? [name(r - 1, c), name(r + 1, c)] : [name(r, c - 1), name(r, c + 1)];
  const glyph = cell.glyph as TextGlyph;
  return (
    <div className={styles.gutterCell} data-gutter={axis} role="img" aria-label={`${a} is ${sign === "lt" ? "less" : "more"} than ${b}`}>
      <span className={styles.gutterSign} style={{ color: puzzleColor(glyph.color, "ember") }}>
        {String(glyph.value ?? "").trim()}
      </span>
    </div>
  );
}

/**
 * Text lines (Logic Grid): "Label: values" lines become a key panel, "1. …"
 * lines numbered clues (the number in accent mono, as on board 16 L7), anything
 * else a plain line.
 */
function TextLines({ lines }: { lines: string[] }) {
  const facts: [string, string][] = [];
  const rest: { n: string | null; text: string }[] = [];
  for (const line of lines) {
    const numbered = /^(\d{1,2})[.)]\s+(.+)$/.exec(line.trim());
    const fact = /^([^:]{1,24}):\s+(.+)$/.exec(line.trim());
    if (numbered) rest.push({ n: numbered[1]!.padStart(2, "0"), text: numbered[2]! });
    else if (fact && rest.length === 0) facts.push([fact[1]!, fact[2]!]);
    else rest.push({ n: null, text: line });
  }
  return (
    <div className={styles.lines}>
      {facts.length ? (
        <dl className={styles.linesFacts}>
          {facts.map(([k, v], i) => (
            <div key={i}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {rest.length ? (
        <ol className={styles.linesList}>
          {rest.map((l, i) => (
            <li key={i} data-numbered={l.n ? true : undefined}>
              {l.n ? (
                <span className={styles.lineNo} aria-hidden>
                  {l.n}
                </span>
              ) : null}
              <span>{l.n ? `${l.text}` : l.text}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Phase 5 boards

const pairLabels = (board: PairBoard): [string, string] => {
  const labels = Array.isArray(board.labels) ? board.labels : [];
  const at = (i: number, fallback: string) => (typeof labels[i] === "string" && labels[i]!.trim() ? labels[i]!.trim() : fallback);
  return [at(0, "Left board"), at(1, "Right board")];
};

/**
 * board.pair@1: two boards side by side (or stacked when `layout` is vertical),
 * each captioned, with an optional arrow from left to right. Only the RIGHT
 * board takes cellSet taps and shows answer cells; the left one is reference.
 */
function PairBoardView({ board, ctx }: { board: PairBoard; ctx: RenderContext }) {
  const vertical = board.layout === "vertical";
  const arrow = board.connector === "arrow";
  const hasLabels = Array.isArray(board.labels) && board.labels.length > 0;
  const [leftLabel, rightLabel] = pairLabels(board);
  const leftCtx: RenderContext = { ...ctx, answerCells: null, play: null };
  const half = (inner: Board | undefined, hctx: RenderContext, label: string, side: "left" | "right") => (
    <div
      className={styles.pairHalf}
      role="group"
      aria-label={side === "right" && ctx.play ? `${label}, the board you tap` : label}
      data-side={side}
    >
      <PairHalfView board={inner} ctx={hctx} />
      {hasLabels ? <div className={styles.pairLabel}>{label}</div> : null}
    </div>
  );
  return (
    <div
      className={styles.pair}
      data-layout={vertical ? "vertical" : "horizontal"}
      data-connector={arrow ? "arrow" : undefined}
      data-target={arrow || undefined}
    >
      {half(board.left, leftCtx, leftLabel, "left")}
      {arrow ? (
        <span className={styles.pairArrow} aria-hidden>
          <svg viewBox="0 0 24 24" className={styles.svg}>
            <path d={vertical ? "M12 4v15M6 13l6 6 6-6" : "M4 12h15M13 6l6 6-6 6"} fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      ) : null}
      {half(board.right, ctx, rightLabel, "right")}
    </div>
  );
}

function PairHalfView({ board, ctx }: { board: Board | undefined; ctx: RenderContext }) {
  switch (kindOf(board)) {
    case "single":
    case "sequence":
    case "matrix":
    case "grid":
      return <BoardView board={board} ctx={ctx} />;
    default:
      // Pairs don't nest, and only single, sequence, matrix and grid boards can be halves.
      return <Unsupported kind={`board.${kindOf(board)}`} detail="not allowed in a pair" />;
  }
}

/**
 * board.scatter@1: a field 1 wide and `aspect` tall. An item's x, y are its
 * centre as fractions of the field's width and height; `size` is a fraction of
 * the WIDTH (a shape's side, a text's em size); `rotation` turns the item about
 * its centre and adds to the glyph's own rotation. Read as "N items": the field
 * never names what is on it (the count asked for, the missing number).
 */
function ScatterBoardView({ board }: { board: ScatterBoard }) {
  const aspect = clampNum(board.aspect, 0.5, 2, 1);
  const items = (Array.isArray(board.items) ? board.items : []).filter(
    (it): it is ScatterItem => Boolean(it) && typeof it === "object" && Boolean(it.glyph),
  );
  return (
    <div className={styles.scatterPanel}>
      <div
        className={styles.scatter}
        role="img"
        aria-label={countLabel(items.length, "item")}
        style={{ aspectRatio: `1 / ${aspect}` }}
        data-items={items.length}
      >
        {items.map((it, i) => (
          <ScatterItemView key={i} item={it} />
        ))}
      </div>
    </div>
  );
}

function ScatterItemView({ item }: { item: ScatterItem }) {
  const size = clampNum(item.size, 0.02, 0.3, 0.08);
  const x = clampNum(item.x, 0, 1, 0.5);
  const y = clampNum(item.y, 0, 1, 0.5);
  const rotation = clampNum(item.rotation, -3600, 3600, 0);
  const g = item.glyph;
  const kind = kindOf(g);
  const style: CSSProperties = {
    left: `${x * 100}%`,
    top: `${y * 100}%`,
    width: `${size * 100}%`,
    transform: `translate(-50%, -50%)${rotation ? ` rotate(${rotation}deg)` : ""}`,
  };
  if (kind === "text") {
    // A text item's em size is `size` × the field width (a two-digit number is about 1.1 em wide).
    const t = g as TextGlyph;
    return (
      <div className={styles.scatterItem} style={style} data-kind="text">
        <span
          className={styles.scatterText}
          data-style={t.style ?? "number"}
          style={{ fontSize: `${size * 100}cqw`, color: puzzleColor(t.color) }}
        >
          {String(t.value ?? "")}
        </span>
      </div>
    );
  }
  return (
    <div className={styles.scatterItem} style={style} data-kind={kind}>
      {kind === "shape" ? <ShapeView glyph={g as ShapeGlyph} radius={46} /> : <GlyphView glyph={g} />}
    </div>
  );
}

function describeCards(board: CardsBoard): string {
  const cards = cardList(board);
  const rule = typeof board.rule === "string" && board.rule.trim() ? ` Rule: ${board.rule.trim()}` : "";
  return `${countLabel(cards.length, "speaker")}.${rule} ${cards.map(cardSays).join(" ")}`.trim();
}

const cardList = (board: CardsBoard): SpeakerCard[] =>
  (Array.isArray(board.cards) ? board.cards : []).filter((c): c is SpeakerCard => Boolean(c) && typeof c === "object");

const quoted = (text: string) => (/^[“"]/.test(text) ? text : `“${text}”`);
const cardSays = (c: SpeakerCard) => `${String(c.name ?? "Someone")} says: ${quoted(String(c.text ?? ""))}`;

/**
 * board.cards@1 (Truth or Lie): the rule above a column of speaker cards, each
 * with its avatar orb, name and statement. Screen readers get the rule, then
 * "Nora says: “…”" per card.
 */
function CardsBoardView({ board }: { board: CardsBoard }) {
  const cards = cardList(board);
  const rule = typeof board.rule === "string" ? board.rule.trim() : "";
  return (
    <div className={styles.cards}>
      {rule ? <p className={styles.cardsRule}>{rule}</p> : null}
      <ul className={styles.cardList} aria-label="Speakers">
        {cards.map((c, i) => {
          const orb = Number.isInteger(c.orb) ? (((c.orb as number) % 6) + 6) % 6 : i % 6;
          return (
            <li key={i} className={styles.card} aria-label={cardSays(c)} data-orb={orb}>
              <span
                className={styles.cardOrb}
                aria-hidden
                style={{ "--orb-light": `var(--color-avatar-orbs-${orb}-0)`, "--orb-dark": `var(--color-avatar-orbs-${orb}-1)` } as CSSProperties}
              />
              <span className={styles.cardBody} aria-hidden>
                <span className={styles.cardName}>{String(c.name ?? "")}</span>
                <span className={styles.cardQuote}>{quoted(String(c.text ?? ""))}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** "Road map with 8 towns and 13 roads": what the map holds, never a route or a time. */
export function graphSummary(layout: Pick<GraphLayout, "nodes" | "edges">): string {
  return `Road map with ${countLabel(layout.nodes.length, "town")} and ${countLabel(layout.edges.length, "road")}`;
}

/** "A to B, 5" (", one way" when directed): the road as the map draws it. */
export function roadLabel(e: Pick<GraphEdgeDrawing, "fromLabel" | "toLabel" | "weight" | "directed">): string {
  return `${e.fromLabel} to ${e.toLabel}${e.weight !== null ? `, ${e.weight}` : ""}${e.directed ? ", one way" : ""}`;
}

const townLabel = (n: GraphNodeDrawing) => `Town ${n.label}${n.role === "none" ? "" : `, ${n.role}`}`;

/**
 * board.graph@1 (Road Map, board 19 P5): one SVG on a card panel, drawn in three layers: straight roads
 * (with an arrowhead at `to` when directed), the weights in ember on bg-coloured pills sitting on their
 * roads (graphLayout moves a pill along or beside its road when the middle is taken, so no label covers
 * a town or another road), then the towns, lettered, the start in ember and the goal in accent as on the
 * board. Static: no effects, filters or animation. Screen readers get "Road map with N towns and M roads",
 * then each road ("A to B, 5") and town ("Town A, start").
 */
function GraphBoardView({ board }: { board: GraphBoard }) {
  const layout = graphLayout(board);
  const r = layout.nodeRadius;
  return (
    <div className={styles.graphPanel}>
      <svg
        className={styles.graph}
        viewBox={`${layout.viewBox.x} ${layout.viewBox.y} ${layout.viewBox.width} ${layout.viewBox.height}`}
        role="group"
        aria-label={graphSummary(layout)}
        data-towns={layout.nodes.length}
        data-roads={layout.edges.length}
      >
        <g className={styles.graphRoads}>
          {layout.edges.map((e, i) => (
            <g key={i} role="img" aria-label={roadLabel(e)} data-road={`${e.from}-${e.to}`}>
              <line x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} />
              {e.arrow ? <polygon className={styles.graphArrow} points={e.arrow.map((p) => p.join(",")).join(" ")} data-arrow /> : null}
            </g>
          ))}
        </g>
        <g aria-hidden>
          {layout.edges.map((e, i) =>
            e.pill ? (
              <g key={i} className={styles.graphWeight} data-weight={`${e.from}-${e.to}`} data-placement={e.placement ?? undefined}>
                <rect
                  x={e.pill.x - e.pill.width / 2}
                  y={e.pill.y - e.pill.height / 2}
                  width={e.pill.width}
                  height={e.pill.height}
                  rx={e.pill.height / 2}
                />
                <text x={e.pill.x} y={e.pill.y} fontSize={e.pill.fontSize}>
                  {e.weight}
                </text>
              </g>
            ) : null,
          )}
        </g>
        {layout.nodes.map((n) => (
          <g key={n.id} className={styles.graphTown} role="img" aria-label={townLabel(n)} data-town={n.id} data-role={n.role}>
            <circle cx={n.x} cy={n.y} r={r} />
            <text x={n.x} y={n.y} fontSize={n.fontSize} aria-hidden>
              {n.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------- answers

function AnswerView({
  answerSpec,
  shown,
  interactive,
  selection,
  disabled,
  onSelect,
  selectedCount,
  ruledOut,
}: {
  answerSpec: PuzzleSpec["answer"] | undefined;
  shown: Answer | null;
  interactive: boolean;
  selection: Selection | null;
  disabled: boolean;
  onSelect: (id: string) => void;
  selectedCount: number;
  /** Options a hint ruled out (dimmed, not pickable). */
  ruledOut?: ReadonlySet<string>;
}) {
  if (!answerSpec || typeof answerSpec !== "object") return <Unsupported kind={`answer.${kindOf(answerSpec)}`} />;
  switch (answerSpec.kind) {
    case "choice": {
      const options = Array.isArray(answerSpec.options) ? answerSpec.options : [];
      const layout = ["row4", "row5", "grid2x2", "column"].includes(answerSpec.layout) ? answerSpec.layout : "row4";
      // Column options, and 2×2 options that are all words or labels, are pills with one line of text (boards L6, L8).
      const pill = layout === "column" || (layout === "grid2x2" && options.length > 0 && options.every((o) => isPillGlyph(o?.glyph)));
      const correctId = shown?.kind === "choice" ? shown.value : null;
      const pickedId = selection?.kind === "choice" ? selection.value : null;
      return (
        <div className={styles.answer}>
          <div className={styles.optionsLabel}>Options</div>
          <div
            className={styles.options}
            data-layout={layout}
            data-pill={(pill && layout !== "column") || undefined}
            role={interactive ? "group" : "list"}
            aria-label={interactive ? "Options" : undefined}
          >
            {options.map((o, i) => {
              const id = o?.id ?? "?";
              const out = ruledOut?.has(id) === true;
              const state: TileState =
                correctId !== null
                  ? id === correctId
                    ? "correct"
                    : interactive && id === pickedId
                      ? "wrong"
                      : "dim"
                  : out
                    ? "dim"
                    : interactive && id === pickedId
                      ? "selected"
                      : "idle";
              const label = `Option ${id}${state === "correct" ? " (correct)" : state === "wrong" ? " (your pick)" : out ? " (ruled out)" : ""}`;
              const body = (
                <>
                  <span className={styles.optionId}>{id}</span>
                  <div className={styles.optionGlyph}>
                    {pill ? (
                      <TextFit.Provider value="line">
                        <GlyphView glyph={o?.glyph} />
                      </TextFit.Provider>
                    ) : (
                      <GlyphView glyph={o?.glyph} />
                    )}
                  </div>
                  {state === "correct" ? (
                    <span className={styles.correctMark} aria-hidden>
                      ✓
                    </span>
                  ) : null}
                </>
              );
              return interactive ? (
                <button
                  key={o?.id ?? i}
                  type="button"
                  aria-pressed={id === pickedId && !out}
                  aria-disabled={disabled || out || undefined}
                  className={styles.option}
                  data-state={state}
                  data-layout={layout}
                  data-pill={(pill && layout !== "column") || undefined}
                  data-option={id}
                  data-ruled-out={out || undefined}
                  aria-label={label}
                  onClick={() => {
                    if (!out) onSelect(id);
                  }}
                >
                  {body}
                </button>
              ) : (
                <div
                  key={o?.id ?? i}
                  role="listitem"
                  className={styles.option}
                  data-state={state}
                  data-layout={layout}
                  data-pill={(pill && layout !== "column") || undefined}
                  aria-label={label}
                >
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "cellSet": {
      const count = clampInt(answerSpec.count, 0, 999, 0);
      return (
        <div className={styles.answer}>
          <div className={styles.cellSetHint} aria-live={interactive ? "polite" : undefined}>
            Tap <strong>{count}</strong> {count === 1 ? "square" : "squares"}
            {interactive && !shown ? (
              <span className={styles.cellSetCount}>
                {" "}
                · {selectedCount} of {count} picked
              </span>
            ) : null}
            {shown?.kind === "cellSet" ? <span className={styles.cellSetRevealed}> · answer cells highlighted</span> : null}
          </div>
        </div>
      );
    }
    default:
      return <Unsupported kind={`answer.${kindOf(answerSpec)}`} />;
  }
}

// ---------------------------------------------------------------- helpers

/** An option glyph that reads as a line of text: a word, label or code of 3+ characters, or a stack with a label. */
function isPillGlyph(g: Glyph | undefined): boolean {
  if (kindOf(g) === "text") {
    const t = g as TextGlyph;
    return ["word", "label", "equation"].includes(t.style ?? "") || [...String(t.value ?? "")].length >= 3;
  }
  if (kindOf(g) === "stack" && (g as { layout?: string }).layout !== "overlay") {
    return (Array.isArray((g as { items?: unknown }).items) ? (g as { items: Glyph[] }).items : []).some((i) => kindOf(i) === "text");
  }
  return false;
}

function clampNum(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return Math.min(max, Math.max(min, n));
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : fallback;
  return Math.min(max, Math.max(min, n));
}

class PartBoundary extends Component<
  { label: string; resetKey: string; children: ReactNode },
  { failed: string | null; key: string }
> {
  override state = { failed: null as string | null, key: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { failed: error instanceof Error ? error.message : String(error) };
  }

  static getDerivedStateFromProps(
    props: { resetKey: string },
    state: { failed: string | null; key: string },
  ): Partial<{ failed: string | null; key: string }> | null {
    return props.resetKey !== state.key ? { failed: null, key: props.resetKey } : null;
  }

  override componentDidCatch(_error: unknown, _info: ErrorInfo) {
    // Swallowed on purpose: a malformed component must never take the page down.
  }

  override render() {
    if (this.state.failed) return <Unsupported kind={this.props.label} detail="render error" />;
    return this.props.children;
  }
}
